import { useInfiniteQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { History, Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import {
  type NotificationHistory,
  notificationHistoryInfiniteQueryOptions,
} from '@/entities/notification';
import { DataTable } from '@/shared/ui/data-table';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { WORKFLOW_CHANNEL_TYPE } from '@/types/workflowConst';

type AlertHistoryRecord = {
  name: string;
  via: string;
  alertAt: string;
};

const LIMIT = 100;

const columns: ColumnDef<AlertHistoryRecord, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 200,
  },
  {
    accessorKey: 'via',
    header: 'Via',
    size: 100,
  },
  {
    accessorKey: 'alertAt',
    header: 'Alert at',
    size: 200,
  },
];

/**
 * NotificationHistory -> AlertHistoryRecord 변환
 */
function mapHistoryToRecord(history: NotificationHistory): AlertHistoryRecord {
  const snapshot = history.snapshot;
  const channelType = snapshot.channel?.type;
  const via =
    channelType === WORKFLOW_CHANNEL_TYPE.MAIL
      ? 'Email'
      : channelType === WORKFLOW_CHANNEL_TYPE.SLACK
        ? 'Slack'
        : '-';

  return {
    name: snapshot.title || '-',
    via,
    alertAt: new Date(history.created_at).toLocaleString(),
  };
}

export type SignalAlertHistoryDialogProps = {
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  alertIds: string[];
};

export function SignalAlertHistoryDialog({
  isOpen,
  close,
  unmount,
  alertIds,
}: SignalAlertHistoryDialogProps) {
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery(notificationHistoryInfiniteQueryOptions(alertIds, LIMIT));

  // 페이지 데이터 flatten 및 변환
  const records = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flat().map(mapHistoryToRecord);
  }, [data?.pages]);

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="m">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-5" />
            Alert history
          </DialogTitle>
          <DialogDescription>
            Records are retained for 30 days only.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="max-h-[60vh]">
          <DataTable
            columns={columns}
            data={records}
            loading={isLoading}
            emptyState={
              <div className="py-8 text-center text-gray-500">
                No alert history found.
              </div>
            }
          />

          {/* Load more 버튼 */}
          {hasNextPage && (
            <div className="flex justify-center py-4">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <div />
          <DialogClose>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
